/* Pen Library Top 20 — chart renderer + privacy-respecting activity beacons.
 * - Renders the latest finalized ranking from the backend; never invents movement.
 * - If the backend is unreachable, shows the last verified ranking this browser saw,
 *   otherwise leaves the server-rendered chart untouched.
 * - Sends events only with analytics consent (Shopify Customer Privacy API) and never with GPC.
 * - Theme previews/editor send "preview" events that the backend validates but never counts.
 */
(function () {
  'use strict';
  var script = document.currentScript;
  var API = (script && script.getAttribute('data-endpoint')) || '';
  var PRODUCT = (script && script.getAttribute('data-product-handle')) || '';
  var CACHE_KEY = 'pp_rank_last_verified_v1';
  var VID_KEY = 'pp_rank_vid_v1';
  if (!API) return;

  var S = window.Shopify || {};
  var ENV = (S.designMode || (S.theme && S.theme.role && S.theme.role !== 'main')) ? 'preview' : 'live';

  function store(kind) { try { var s = window[kind]; var k = '__pp_t'; s.setItem(k, k); s.removeItem(k); return s; } catch (e) { return null; } }
  var local = store('localStorage');

  // ---------------- Chart rendering ----------------
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function cover(url) { if (!url) return ''; return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=200'; }

  // Verified indicators only: the movement chip comes from the published snapshot (never inferred here),
  // and badges are shown only if the snapshot lists them.
  var BADGES = { 'MOST DISCOVERED': 'Most Discovered', 'BOOK CLUB PICK': 'Book Club Pick', 'READER FAVORITE': 'Reader Favorite' };
  function chip(cls, html, aria) { return '<span class="pen-chip ' + cls + '" aria-label="' + esc(aria) + '">' + html + '</span>'; }
  function movementChip(m) {
    if (!m || !m.type) return '';
    var d = Math.max(0, Math.floor(Number(m.delta) || 0));
    var places = d === 1 ? ' place' : ' places';
    switch (m.type) {
      case 'UP': return chip('pen-chip-up', '▲ Rising <small>+' + d + '</small>', 'Rising: up ' + d + places + ' since last week');
      case 'DOWN': return chip('pen-chip-down', '▼ Falling <small>−' + d + '</small>', 'Falling: down ' + d + places + ' since last week');
      case 'HOLDING': return chip('pen-chip-hold', 'Holding', 'Holding: same position as last week');
      case 'RETURNING': return chip('pen-chip-ret', 'Returning', 'Returning to the chart');
      case 'NEW': return chip('pen-chip-new', 'New', 'New this week');
      default: return '';
    }
  }
  function indicators(e) {
    return movementChip(e.movement) + (e.badges || []).filter(function (b) { return BADGES[b]; })
      .map(function (b) { return chip('pen-chip-badge', BADGES[b], BADGES[b]); }).join('');
  }

  function rowHtml(e) {
    var href = esc(e.url);
    var img = e.image_url
      ? '<img class="pen-cover" src="' + esc(cover(e.image_url)) + '" alt="' + esc(e.title) + ' cover" loading="lazy" width="100" height="180">'
      : '<span class="pen-cover pen-cover-placeholder">Cover coming soon</span>';
    return '<li class="pen-rank-row" data-handle="' + esc(e.handle) + '">' +
      '<strong class="pen-rank-num">' + Number(e.position) + '</strong>' +
      '<div><a class="pen-cover-link" href="' + href + '">' + img + '</a></div>' +
      '<div class="pen-rank-info"><strong class="pen-rank-title"><a href="' + href + '">' + esc(e.title) + '</a></strong>' +
      (e.subtitle ? '<div class="pen-rank-sub">' + esc(e.subtitle) + '</div>' : '') +
      '<div class="pen-ind" aria-label="Weekly indicators">' + indicators(e) + '</div>' +
      '<a class="pen-rank-view" href="' + href + '">View Book →</a></div></li>';
  }

  function render(chart, root) {
    var list = root.querySelector('.pen-rank-list');
    if (!list || !chart || !chart.entries || !chart.entries.length) return false;
    list.innerHTML = chart.entries.map(rowHtml).join('\n');
    leaveLaunchState(root, list);
    var cap = root.querySelector('[data-pen-week]');
    if (cap) {
      cap.textContent = chart.caption || ('Week of ' + chart.week_label + (chart.compared_to ? ' · Movement compared with the week of ' + chart.compared_to.week_label : ' · First verified chart'));
      cap.hidden = false;
    }
    root.setAttribute('data-pen-state', 'verified');
    return true;
  }

  // Launch showcase (before any verified chart): no positions are claimed, and rows are shuffled per page view
  // so no book gets a systematic top-of-list advantage in chart impressions while activity is collected.
  function leaveLaunchState(root, list) {
    if (list) { list.classList.remove('pen-launch'); list.removeAttribute('data-pen-launch'); }
    [].forEach.call(root.querySelectorAll('[data-pen-status],[data-pen-status-note]'), function (el) { el.hidden = true; });
  }
  function shuffleLaunch(root) {
    var list = root.querySelector('.pen-rank-list[data-pen-launch]');
    if (!list) return;
    var rows = [].slice.call(list.children);
    for (var i = rows.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1)), t = rows[i]; rows[i] = rows[j]; rows[j] = t;
    }
    rows.forEach(function (r) { list.appendChild(r); });
  }

  // Preliminary Top 20 (before the first verified weekly edition): positions only for books with verified
  // activity, everything else marked "Awaiting Reader Activity". No movement, no badges; never cached as verified.
  function prelimRowHtml(e) {
    var href = esc(e.url);
    var img = e.image_url
      ? '<img class="pen-cover" src="' + esc(cover(e.image_url)) + '" alt="' + esc(e.title) + ' cover" loading="lazy" width="100" height="180">'
      : '<span class="pen-cover pen-cover-placeholder">Cover coming soon</span>';
    var num = e.position ? '<strong class="pen-rank-num">' + Number(e.position) + '</strong>'
      : '<span class="pen-rank-num pen-rank-none" aria-hidden="true">–</span>';
    var ind = e.position ? '' : chip('pen-chip-wait', 'Awaiting Reader Activity', 'Awaiting reader activity: not yet ranked');
    return '<li class="pen-rank-row' + (e.position ? '' : ' pen-rank-awaiting') + '" data-handle="' + esc(e.handle) + '">' + num +
      '<div><a class="pen-cover-link" href="' + href + '">' + img + '</a></div>' +
      '<div class="pen-rank-info"><strong class="pen-rank-title"><a href="' + href + '">' + esc(e.title) + '</a></strong>' +
      '<div class="pen-ind" aria-label="Preliminary status">' + ind + '</div>' +
      '<a class="pen-rank-view" href="' + href + '">View Book →</a></div></li>';
  }
  function shuffled(a) {
    a = a.slice();
    for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)), t = a[i]; a[i] = a[j]; a[j] = t; }
    return a;
  }
  function renderPreliminary(chart, root, firstPublish) {
    var list = root.querySelector('.pen-rank-list');
    if (!list || !chart || !chart.entries || !chart.entries.length) return false;
    var ranked = chart.entries.filter(function (e) { return e.position; }).sort(function (a, b) { return a.position - b.position; });
    // Unranked books have no fixed order: shuffled on first render, then kept in the same order on each refresh.
    var prevOrder = root._penOrder || [];
    var unranked = chart.entries.filter(function (e) { return !e.position; });
    var kept = prevOrder.map(function (h) { return unranked.filter(function (e) { return e.handle === h; })[0]; }).filter(Boolean);
    var awaiting = kept.concat(shuffled(unranked.filter(function (e) { return prevOrder.indexOf(e.handle) < 0; })));
    root._penOrder = awaiting.map(function (e) { return e.handle; });
    list.innerHTML = ranked.concat(awaiting).map(prelimRowHtml).join('\n');
    list.classList.remove('pen-launch'); list.removeAttribute('data-pen-launch');
    var pill = root.querySelector('.pen-status-pill'), txt = root.querySelector('.pen-status-text');
    var note = root.querySelector('[data-pen-status-note]'), cap = root.querySelector('[data-pen-week]');
    var first = firstPublish ? new Date(firstPublish).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York' }) : '';
    var n = ranked.length;
    if (pill) pill.textContent = chart.live ? 'Live · Preliminary Top 20' : 'Preliminary Top 20';
    if (txt) txt.textContent = (n ? n + (n === 1 ? ' book' : ' books') + ' ranked from verified reader activity'
      : 'Positions are assigned automatically from verified reader activity') + ' · ' + chart.activity_label +
      (chart.live && chart.computed_at ? ' · Updated ' + new Date(chart.computed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' : '');
    if (note) note.textContent = chart.note || 'This is a preliminary edition, not an official weekly ranking. Books are ranked only where verified reader activity exists; the others are awaiting reader activity. This list stays fixed until the first fully verified weekly edition.';
    if (cap) { cap.textContent = first ? 'First fully verified weekly edition: ' + first + '.' : ''; cap.hidden = !first; }
    root.setAttribute('data-pen-state', 'preliminary');
    root.setAttribute('data-pen-ranked', String(n));
    return true;
  }

  function setStatus(root, pillText, text) {
    var pill = root.querySelector('.pen-status-pill'), txt = root.querySelector('.pen-status-text');
    if (pill) pill.textContent = pillText;
    if (txt) txt.textContent = text;
  }

  function cached() { try { return JSON.parse(local && local.getItem(CACHE_KEY)); } catch (e) { return null; } }

  var REFRESH_MS = 15 * 60 * 1000;   // matches the backend's 15-minute live recompute
  function initChart(root) {
    shuffleLaunch(root);
    loadChart(root, true);
    setInterval(function () {
      if (document.visibilityState === 'hidden') { root._penStale = true; return; }
      loadChart(root, false);
    }, REFRESH_MS);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && root._penStale) { root._penStale = false; loadChart(root, false); }
    });
  }

  // first: initial page load. Later refreshes never replace a shown list with an error or empty state.
  function loadChart(root, first) {
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 5000);
    return fetch(API + '/api/chart', { credentials: 'omit', cache: 'no-store', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (j) {
        clearTimeout(timer);
        if (j.status === 'published' && j.chart) {
          var prev = cached();
          var best = prev && prev.week_id > j.chart.week_id ? prev : j.chart;
          render(best, root);
          try { local && local.setItem(CACHE_KEY, JSON.stringify(best)); } catch (e) {}
        } else if (j.status === 'preliminary' && j.chart && !cached()) {
          renderPreliminary(j.chart, root, j.first_publish);
        } else if (first) {
          var c = cached(); if (c) render(c, root);
          if (!c) setStatus(root, 'Live Top 20', 'Positions are assigned automatically as soon as books receive verified reader activity');
          var cap = root.querySelector('[data-pen-week]');
          if (cap && !c) {
            cap.textContent = j.first_publish
              ? 'First fully verified weekly edition: ' + new Date(j.first_publish).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York' }) + '.'
              : 'The first fully verified weekly edition follows the first complete tracked week.';
            cap.hidden = false;
          }
        }
      })
      .catch(function () {
        clearTimeout(timer);
        if (!first) return;
        var c = cached();
        if (c) render(c, root); else setStatus(root, 'Pen Library Top 20', 'Live rankings are temporarily unavailable. Please check back shortly.');
      })
      .then(function () { if (first) observeImpressions(root); else rearmImpressions(); });
  }

  // ---------------- Consent-gated events ----------------
  var queue = [], pending = [], vid = null, consented = false, decided = false;
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16); crypto.getRandomValues(b); b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    var h = Array.prototype.map.call(b, function (x) { return (x + 256).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }
  function visitorId() {
    if (vid) return vid;
    var s = local || store('sessionStorage');
    if (!s) return null;
    vid = s.getItem(VID_KEY);
    if (!vid) { vid = uuid(); s.setItem(VID_KEY, vid); }
    return vid;
  }
  function push(ev) {
    ev.ts = Date.now();
    if (!decided) { if (pending.length < 40) pending.push(ev); return; }
    if (!consented) return;
    queue.push(ev); if (queue.length >= 20) flush();
  }
  function flush() {
    if (!queue.length || !consented) return;
    var id = visitorId(); if (!id) { queue = []; return; }
    var body = JSON.stringify({ v: 1, vid: id, consent: true, wd: navigator.webdriver === true, env: ENV, events: queue.splice(0, 40) });
    var url = API + '/api/events';
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) {}
    try { fetch(url, { method: 'POST', body: body, headers: { 'content-type': 'text/plain' }, keepalive: true, credentials: 'omit' }); } catch (e) {}
  }

  function withConsent(cb) {
    if (navigator.globalPrivacyControl === true) return cb(false);
    function check() { try { cb(!!S.customerPrivacy.analyticsProcessingAllowed()); } catch (e) { cb(false); } }
    if (S.customerPrivacy) return check();
    if (S.loadFeatures) {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (err) cb(false); else check(); });
    } else cb(false);
  }

  var seen = {}, impressionIO = null, chartRoot = null;
  // Re-arm impression tracking for rows not yet counted (used when a visitor grants consent after the page loaded).
  function rearmImpressions() {
    if (!impressionIO || !chartRoot) return;
    chartRoot.querySelectorAll('.pen-rank-row[data-handle]').forEach(function (el) {
      if (!seen[el.getAttribute('data-handle')]) { impressionIO.unobserve(el); impressionIO.observe(el); }
    });
  }
  function observeImpressions(root) {
    if (!('IntersectionObserver' in window)) return;
    var timers = {};
    chartRoot = root;
    var io = impressionIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var h = en.target.getAttribute('data-handle'); if (!h || seen[h]) return;
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          timers[h] = setTimeout(function () {
            if (decided && !consented) return;   // not counted, and left armed in case consent is granted later
            seen[h] = 1; push({ t: 'chart_impression', h: h }); io.unobserve(en.target);
          }, 1000);
        } else { clearTimeout(timers[h]); }
      });
    }, { threshold: [0, 0.5] });
    root.querySelectorAll('.pen-rank-row[data-handle]').forEach(function (el) { io.observe(el); });
  }

  function trackProduct() {
    var visible = 0, last = document.visibilityState === 'visible' ? Date.now() : 0, sent = false;
    function tickVis() {
      if (sent) return;
      if (last) { visible += Date.now() - last; last = document.visibilityState === 'visible' ? Date.now() : 0; }
      if (visible >= 3000) { sent = true; push({ t: 'book_view', h: PRODUCT, dwell: visible }); flush(); }
    }
    document.addEventListener('visibilitychange', function () { tickVis(); if (document.visibilityState === 'visible') last = Date.now(); });
    var iv = setInterval(function () { tickVis(); if (sent) clearInterval(iv); }, 1000);
    var done = {};
    function once(k) { if (done[k]) return; done[k] = 1; push({ t: 'interaction', h: PRODUCT, k: k }); flush(); }
    document.addEventListener('submit', function (e) {
      var f = e.target; if (f && f.action && f.action.indexOf('/cart/add') > -1) once('add_to_cart');
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target : null; if (!t) return;
      if (t.closest('share-button, .share-button, [data-share]')) return once('share');
      var a = t.closest('a[href]');
      if (a && a.host && a.host !== location.host && t.closest('.product')) once('buy_link');
    }, true);
  }

  // Free PDF download. The section server-renders a "First edition coming soon" panel; the real
  // download button is revealed only for a verified edition whose link passes the safety checks.
  // The button is never shown with a placeholder href.
  function initDownload(root) {
    var box = root.querySelector('[data-pen-download]');
    if (!box) return;
    var a = box.querySelector('[data-pen-download-link]');
    var soon = box.querySelector('[data-pen-download-soon]');
    var title = box.querySelector('[data-pen-download-title]');
    var meta = box.querySelector('[data-pen-download-meta]');
    function set(state, t, m) {
      box.setAttribute('data-state', state);
      if (title && t) title.textContent = t;
      if (meta && m) meta.textContent = m;
      if (a) a.hidden = state !== 'ready';
      if (soon) soon.hidden = state === 'ready';
      if (soon && state === 'unavailable') soon.textContent = 'Check back soon';
    }
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 8000);
    fetch(API + '/api/edition', { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (j) {
        clearTimeout(timer);
        if (!j || !j.available) return;                      // keep the server-rendered "coming soon" panel
        var u; try { u = new URL(j.url); } catch (e) { return; }
        if (u.origin !== new URL(API).origin || !/^\/editions\/\d{4}-\d{2}-\d{2}\.pdf$/.test(u.pathname) || u.search || u.hash) return;
        if (!a) return;
        a.href = u.href;
        set('ready', 'This week’s Top 20, ready to keep', 'Free 3-page PDF · Week of ' + j.week_label + ' · No sign-up needed');
      })
      .catch(function () {
        clearTimeout(timer);
        set('unavailable', 'Weekly PDF edition', 'The free PDF can’t be loaded right now. Please check back shortly — no sign-up needed.');
      });
  }

  function start() {
    var root = document.querySelector('[data-pen-chart]');
    if (root) { initChart(root); initDownload(root); }
    withConsent(function (ok) {
      consented = ok; decided = true;
      var early = pending; pending = [];
      if (!ok) return;
      early.forEach(function (e) { queue.push(e); });
      push({ t: 'chart_load' });
      if (PRODUCT) trackProduct();
      setInterval(flush, 4000);
      flush();
    });
    document.addEventListener('visitorConsentCollected', function () {
      withConsent(function (ok) {
        var was = consented; consented = ok; decided = true;
        if (ok && !was) {
          push({ t: 'chart_load' });
          rearmImpressions();
          if (PRODUCT) trackProduct();
          setInterval(flush, 4000);
          flush();
        }
      });
    });
    addEventListener('pagehide', flush);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
